import { COUNTRIES } from '@/helpers/countries';
import { SelectFieldItem } from '@pg-web/bearded-dragon-ui';

export const IS_DEV = import.meta.env.DEV;
export const IS_TEST = import.meta.env.VITE_ENV === 'test';
export const IS_STAGING =
  import.meta.env.PROD && import.meta.env.VITE_ENV === 'stg';
export const IS_PROD =
  import.meta.env.PROD && import.meta.env.VITE_ENV === 'prd';

export const API_URL = import.meta.env.VITE_API_URL;

export const GRAPHQL_API_ENDPOINT = `${API_URL}/graphql`;

export const SEQUENCING_API_URL = import.meta.env.VITE_SEQUENCING_API_URL;

export const CHATPG_CONVERSATION_URL = import.meta.env
  .VITE_CHATPG_CONVERSATION_URL;
export const CHATPG_LOGIN_URL = import.meta.env.VITE_CHATPG_LOGIN_URL;
export const PG_HOME_URL = 'https://www.probablygenetic.com';

export const INTAKE_FORM_DISCLAIMER = `If you continue, we will ask you questions about your and your family's medical history and other health information. We use this information you provide to evaluate your eligibility for genetic testing. By clicking "Continue", you authorize us to collect the health information you provide to us.`;

export const PRIVACY_POLICY_URL =
  'https://www.probablygenetic.com/legal/website-privacy-policy?phId=01904c7c-45b0-705e-9d93-bf9d57acea90';

export const DOB_MONTH_OPTIONS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const SENTRY_SPAN_DENY_LIST = [
  'rudderstack.com',
  'rudderlabs.com',
  'gravatar.com',
  'cloudfront.net/', // posthog reverse proxy
  'cookiebot.com',
  'posthog.com',
];

export const SENTRY_DENY_URLS_LIST = [
  // Facebook flakiness
  /graph\.facebook\.com/i,
  // Facebook blocked
  /connect\.facebook\.net\/en_US\/all\.js/i,
  // Woopra flakiness
  /eatdifferent\.com\.woopra-ns\.com/i,
  /static\.woopra\.com\/js\/woopra\.js/i,
  // Chrome extensions
  /extensions\//i,
  /^chrome:\/\//i,
  /^chrome-extension:\/\//i,
  // Other plugins
  /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
  /webappstoolbarba\.texthelp\.com\//i,
  /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
  /consentcdn\.cookiebot\.com/i,
  /posthog\.com/i,
  /rudderstack\.com/i,
];

export const SENTRY_IGNORE_ERRORS_LIST = [
  // Sentry community
  // Random plugins/extensions
  'top.GLOBALS',
  // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
  'originalCreateNotification',
  'canvas.contentDocument',
  'MyApp_RemoveAllHighlights',
  'http://tt.epicplay.com',
  "Can't find variable: ZiteReader",
  'jigsaw is not defined',
  'ComboSearch is not defined',
  'http://loading.retry.widdit.com/',
  'atomicFindClose',
  // Facebook borked
  'fb_xd_fragment',
  // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
  // reduce this. (thanks @acdha)
  // See http://stackoverflow.com/questions/4113268
  'bmi_SafeAddOnload',
  'EBCallBackMessageReceived',
  // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
  'conduitPage',
  'Network request failed',
  'Failed to fetch',
  'NetworkError',
  'AbortError',
  'withrealtime/messaging',
  'Chartboost is not defined',
  "Can't find variable: Chartboost",
  // PG Custom
  'Multiple faces detected in the photo provided',
  'Unable to detect a face in the photo provided',
];

export enum RelationshipToPatient {
  Self = 'self',
  FamilyMember = 'family_member',
  Parent = 'parent',
}

export enum BiologicalSex {
  Male = 'Male',
  Female = 'Female',
  // OTHER = 'OTHER',
}

export enum PatientEthnicity {
  NATIVE_AMERICAN = 'NATIVE_AMERICAN',
  ASIAN = 'ASIAN',
  BLACK = 'BLACK',
  HISPANIC = 'HISPANIC',
  MIDDLE_EASTERN = 'MIDDLE_EASTERN',
  PACIFIC_ISLANDER = 'PACIFIC_ISLANDER',
  WHITE = 'WHITE',
  OTHER = 'OTHER',
}

export const RELATIONSHIP_OPTIONS = [
  {
    key: RelationshipToPatient.Self,
    value: RelationshipToPatient.Self,
    label: 'Self',
  },
  {
    key: RelationshipToPatient.Parent,
    value: RelationshipToPatient.Parent,
    label: 'Parent',
  },
  {
    key: RelationshipToPatient.FamilyMember,
    value: RelationshipToPatient.FamilyMember,
    label: 'Family Member',
  },
];

export const COUNTRIES_OPTIONS = COUNTRIES.map((country) => ({
  key: country,
  value: country,
  label: country,
}));

export const ETHNICITY_OPTIONS: SelectFieldItem[] = [
  {
    key: 'NATIVE_AMERICAN',
    label: 'American Indian or Alaska Native',
    value: 'NATIVE_AMERICAN',
  },
  { key: 'ASIAN', label: 'Asian', value: 'asian' },
  { key: 'BLACK', label: 'Black or African American', value: 'black' },
  { key: 'HISPANIC', label: 'Hispanic or Latino', value: 'hispanic' },
  {
    key: 'MIDDLE_EASTERN',
    label: 'Middle Eastern or North African',
    value: 'MIDDLE_EASTERN',
  },
  {
    key: 'PACIFIC_ISLANDER',
    label: 'Native Hawaiian or Other Pacific Islander',
    value: 'PACIFIC_ISLANDER',
  },
  { key: 'WHITE', label: 'White', value: 'WHITE' },
  { key: 'OTHER', label: 'Other', value: 'OTHER' },
];

export const BIOLOGICAL_SEX_OPTIONS = [
  {
    key: BiologicalSex.Female,
    value: BiologicalSex.Female,
    label: 'Female',
  },
  {
    key: BiologicalSex.Male,
    value: BiologicalSex.Male,
    label: 'Male',
  },
];

export const STATES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DC',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
];

export const STATE_OPTIONS = STATES.map((state) => ({
  key: state,
  value: state,
  label: state,
}));

export const SPECIALTIES = [
  [1, 'General Practice'],
  [2, 'General Surgery'],
  [3, 'Allergy/Immunology'],
  [4, 'Otolaryngology'],
  [5, 'Anesthesiology'],
  [6, 'Cardiovascular Disease (Cardiology)'],
  [7, 'Dermatology'],
  [8, 'Family Practice'],
  [9, 'Interventional Pain Management'],
  [10, 'Gastroenterology'],
  [11, 'Internal Medicine'],
  [12, 'Osteopathic Manipulative Medicine'],
  [13, 'Neurology'],
  [14, 'Neurosurgery'],
  [15, 'Speech Language Pathologist'],
  [16, 'Obstetrics & Gynecology'],
  [17, 'Hospice and Palliative Care'],
  [18, 'Ophthalmology'],
  [19, 'Oral Surgery (Dentist only)'],
  [20, 'Orthopedic Surgery'],
  [21, 'Clinical Cardiac Electrophysiology'],
  [22, 'Pathology'],
  [23, 'Sports Medicine'],
  [24, 'Plastic and Reconstructive Surgery'],
  [25, 'Physical Medicine and Rehabilitation'],
  [26, 'Psychiatry'],
  [27, 'Geriatric Psychiatry'],
  [28, 'Colorectal Surgery (Proctology)'],
  [29, 'Pulmonary Disease'],
  [30, 'Diagnostic Radiology'],
  [31, 'Anesthesiology Assistant'],
  [32, 'Thoracic Surgery'],
  [33, 'Urology'],
  [34, 'Chiropractic'],
  [35, 'Nuclear Medicine'],
  [36, 'Pediatric Medicine'],
  [37, 'Geriatric Medicine'],
  [38, 'Nephrology'],
  [39, 'Hand Surgery'],
  [40, 'Optometry'],
  [41, 'Certified Nurse Midwife'],
  [42, 'Certified Registered Nurse Anesthetist (CRNA)'],
  [43, 'Infectious Disease'],
  [44, 'Endocrinology'],
  [45, 'Podiatry'],
  [46, 'Nurse Practitioner'],
  [47, 'Individual Certified Orthotist'],
  [48, 'Individual Certified Prosthetist'],
  [49, 'Individual Certified Prosthetist-Orthotist'],
  [50, 'Clinical Psychologist'],
  [51, 'Audiologist'],
  [52, 'Physical Therapist in Private Practice'],
  [53, 'Rheumatology'],
  [54, 'Occupational Therapist in Private Practice'],
  [55, 'Registered Dietitian or Nutrition Professional'],
  [56, 'Pain Management'],
  [57, 'Peripheral Vascular Disease'],
  [58, 'Vascular Surgery'],
  [59, 'Cardiac Surgery'],
  [60, 'Addiction Medicine'],
  [61, 'Licensed Clinical Social Worker'],
  [62, 'Critical Care (Intensivists)'],
  [63, 'Hematology'],
  [64, 'Hematology-Oncology'],
  [65, 'Preventive Medicine'],
  [66, 'Maxillofacial Surgery'],
  [67, 'Neuropsychiatry'],
  [68, 'Medical Oncology'],
  [69, 'Surgical Oncology'],
  [70, 'Radiation Oncology'],
  [71, 'Emergency Medicine'],
  [72, 'Interventional Radiology'],
  [73, 'Optician'],
  [74, 'Physician Assistant'],
  [75, 'Gynecological Oncology'],
  [76, 'Pedorthic personnel'],
  [77, 'Sleep Medicine'],
  [78, 'Interventional Cardiology'],
  [79, 'Dentist'],
  [80, 'Hospitalist'],
  [81, 'Advanced Heart Failure and Transplant Cardiology'],
  [82, 'Medical Toxicology'],
];

SPECIALTIES.sort((a, b) =>
  String(a[1]).toUpperCase().localeCompare(String(b[1]).toUpperCase())
);

export const SPECIALTIES_OPTIONS = SPECIALTIES.map(([id, label]) => ({
  key: String(id),
  value: String(id),
  label,
}));
